import { useEffect, useState } from 'react'
import { useGameStore } from '@/store/gameStore'

const BINANCE_WS = 'wss://stream.binance.com:9443/ws'
const SYMBOLS: Record<string, string> = {
  BTC: 'btcusdt',
  ETH: 'ethusdt',
  SOL: 'solusdt',
}

const STALL_TIMEOUT_MS = 30_000
const BACKOFF_STEPS_MS = [1_000, 5_000, 15_000, 30_000]

export function useLivePrice() {
  const { currentCrypto } = useGameStore()
  const [price, setPrice] = useState<number | null>(null)

  useEffect(() => {
    setPrice(null)
    const symbol = SYMBOLS[currentCrypto]
    if (!symbol) return

    let ws: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let stallTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let disposed = false

    const armStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        // No messages for STALL_TIMEOUT_MS — force reconnect
        if (ws && ws.readyState === WebSocket.OPEN) ws.close()
      }, STALL_TIMEOUT_MS)
    }

    const connect = () => {
      if (disposed) return
      ws = new WebSocket(`${BINANCE_WS}/${symbol}@trade`)
      ws.onopen = () => {
        attempt = 0
        armStallTimer()
      }
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data)
        setPrice(parseFloat(data.p))
        armStallTimer()
      }
      ws.onerror = () => {
        // Let onclose handle reconnect
      }
      ws.onclose = () => {
        if (stallTimer) { clearTimeout(stallTimer); stallTimer = null }
        if (disposed) return
        const delay = BACKOFF_STEPS_MS[Math.min(attempt, BACKOFF_STEPS_MS.length - 1)]
        attempt++
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      // Tab back in foreground — if socket dead or closing, reconnect now
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
        attempt = 0
        connect()
      }
    }

    connect()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibility)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (stallTimer) clearTimeout(stallTimer)
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
        ws.close()
      }
    }
  }, [currentCrypto])

  return price
}
